/** The map editor, wired to the app.
 *
 * Everything the editor screen cannot do for itself lives here: reading and
 * writing a map file, filling the board from the generator, and starting a
 * game on what has been drawn. The screen stays a leaf and this stays a
 * component rather than a hook, because the file input needs somewhere to be.
 */

import { useCallback, useMemo, useRef, useState } from "react";
import type { ContentRegistry } from "@game/core";
import type { EditorDraft } from "@game/ui";
import { MapEditor, blankDraft } from "@game/ui";
import { DEFAULT_TEMPLATE_ID, findTemplate } from "@game/content/mapgen";

import { app } from "../game/app.js";
import { DEFAULT_OPTIONS } from "../game/newgame.js";
import { scenarioFileName, waterTerrains } from "./compile.js";
import { buildGame, downloadDraft, generateIntoDraft, openScenarioFile } from "./host.js";

export interface EditorRouteProps {
  readonly content: ContentRegistry | null;
  readonly onClose: () => void;
}

export function EditorRoute(props: EditorRouteProps): JSX.Element {
  const { content } = props;
  const [draft, setDraft] = useState<EditorDraft | undefined>(undefined);
  const [notice, setNotice] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const water = useMemo(
    () => (content === null ? new Set(["water", "rock"]) : waterTerrains(content)),
    [content],
  );

  const onOpenFile = useCallback((): void => { fileRef.current?.click(); }, []);

  const onPicked = useCallback((file: File | undefined): void => {
    if (file === undefined || content === null) return;
    void openScenarioFile(file, content).then((r) => {
      if (!r.ok) { setNotice(r.message); return; }
      setDraft(r.draft);
      setNotice(r.warning ?? `Opened ${file.name}.`);
    });
  }, [content]);

  const onSaveFile = useCallback((d: EditorDraft): void => {
    if (content === null) { setNotice("The content bundle is still loading."); return; }
    setNotice(downloadDraft(d, content, scenarioFileName(d)).message);
  }, [content]);

  const onGenerate = useCallback((d: EditorDraft): void => {
    if (content === null) { setNotice("The content bundle is still loading."); return; }
    // A fresh seed each time, so pressing it twice gives two boards rather than
    // the same one; the seed travels into the draft and into the saved file.
    const seed = (d.seed + 1013904223) >>> 0;
    const next = generateIntoDraft(d, content, seed, findTemplate(DEFAULT_TEMPLATE_ID));
    setDraft({ ...next, seed });
    setNotice(`Generated a board from seed ${String(seed)}. Everything on it can now be edited.`);
  }, [content]);

  const onPlay = useCallback((d: EditorDraft): void => {
    if (content === null) { setNotice("The content bundle is still loading."); return; }
    const built = buildGame(d, content);
    if (!built.ok) { setNotice(built.message); return; }
    // Resumed rather than started: `newGame` has already built the state, and
    // `resumeGame` is the path that reads who is playing out of the state
    // rather than out of a `GameOptions` this map never had.
    app.resumeGame(built.state, {
      ...DEFAULT_OPTIONS,
      seed: d.seed,
      mapCells: d.width,
      faction: d.players[0]?.faction ?? DEFAULT_OPTIONS.faction,
      opponents: Math.max(0, d.players.filter((p) => p.kind !== "none").length - 1),
      victory: d.victory,
    });
  }, [content]);

  return (
    <>
      <MapEditor
        content={content}
        initial={draft}
        waterTerrains={water}
        notice={notice}
        onOpenFile={onOpenFile}
        onSaveFile={onSaveFile}
        onGenerate={onGenerate}
        onPlay={onPlay}
        onClose={props.onClose}
      />
      {/* Off-screen rather than hidden: a `display:none` input is not
          clickable in every browser, and this one is clicked by a button. */}
      <input
        ref={fileRef}
        type="file"
        accept=".json,application/json"
        style={{ position: "fixed", left: -9999, top: 0, width: 1, height: 1 }}
        onChange={(e) => {
          onPicked(e.currentTarget.files?.[0]);
          // Cleared so that picking the same file twice fires twice.
          e.currentTarget.value = "";
        }}
      />
    </>
  );
}

export { blankDraft };
